import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Play } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "@/components/admin/AdminShell";
import { Kpi, Skeleton, fmt } from "@/pages/admin/AdminPages";
import { ReadinessBadge } from "@/components/StatusBadge";
import api, { apiErrorMessage } from "@/lib/api";

const sel = "rounded-lg border border-hborder bg-white px-3 py-2.5 text-sm outline-none focus:border-tblue";
const READINESS = ["Not started", "In progress", "Action needed", "Under review", "Ready for submission"];

export function AdminCases() {
  const [d, setD] = useState(null);
  const [f, setF] = useState({ q: "", corridor: "", readiness: "", org_id: "", stuck: false, in_review: false });

  useEffect(() => {
    const t = setTimeout(() => {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== false));
      api.get("/admin/cases", { params }).then((r) => setD(r.data)).catch(() => setD(false));
    }, 250);
    return () => clearTimeout(t);
  }, [f]);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));

  return (
    <AdminShell title="Cases & operations" subtitle="Every case across all tenants (read-only)">
      {d === false && <p className="text-sm text-coral">Could not load cases.</p>}
      {d && (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" data-testid="ops-kpis">
          <Kpi testId="ops-kpi-total" label="Total cases" value={d.summary.total} />
          <Kpi testId="ops-kpi-stuck" label={`Stuck (>${d.summary.stuck_days}d idle)`} value={<span className={d.summary.stuck ? "text-coral" : ""}>{d.summary.stuck}</span>} sub="Not ready, no activity" />
          <Kpi testId="ops-kpi-queue" label="Review queue" value={d.summary.review_queue} />
          <Kpi testId="ops-kpi-failed" label="Failed documents" value={<span className={d.summary.failed_documents ? "text-coral" : ""}>{d.summary.failed_documents}</span>} sub="Uploads needing attention" />
        </div>
      )}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input value={f.q} onChange={(e) => set({ q: e.target.value })} placeholder="Search applicant name" data-testid="ops-search" className={`${sel} min-w-[200px] flex-1`} />
        <select value={f.corridor} onChange={(e) => set({ corridor: e.target.value })} className={sel} data-testid="ops-corridor"><option value="">All corridors</option>{d?.corridors.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select value={f.readiness} onChange={(e) => set({ readiness: e.target.value })} className={sel} data-testid="ops-readiness"><option value="">All readiness</option>{READINESS.map((r) => <option key={r}>{r}</option>)}</select>
        <select value={f.org_id} onChange={(e) => set({ org_id: e.target.value })} className={sel} data-testid="ops-org"><option value="">All agencies</option>{d?.organizations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.stuck} onChange={(e) => set({ stuck: e.target.checked })} data-testid="ops-stuck" /> Stuck only</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.in_review} onChange={(e) => set({ in_review: e.target.checked })} data-testid="ops-inreview" /> In review queue</label>
      </div>
      {d === null ? <Skeleton /> : d.cases.length === 0 ? (
        <p className="rounded-md border border-dashed border-hborder bg-white p-10 text-center text-sm text-muted" data-testid="ops-empty">No cases match these filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-hborder bg-white hv-scroll-thin">
          <table className="w-full min-w-[820px] text-left text-sm" data-testid="ops-cases-table">
            <thead className="border-b border-hborder bg-softcream text-xs uppercase tracking-wider text-muted">
              <tr><th className="px-4 py-3">Applicant</th><th className="px-4 py-3">Route</th><th className="px-4 py-3">Agency</th><th className="px-4 py-3">Readiness</th><th className="px-4 py-3">Findings</th><th className="px-4 py-3">Updated</th></tr>
            </thead>
            <tbody>
              {d.cases.map((c) => (
                <tr key={c.id} className="border-b border-hborder/60 last:border-0" data-testid={`ops-case-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-heading">{c.applicant_name}{c.stuck && <span className="ml-2 rounded bg-coral/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-coral">Stuck</span>}</td>
                  <td className="px-4 py-3 text-xs text-muted">{c.corridor_key} · {c.visa_category} · v{c.requirement_set_version}</td>
                  <td className="px-4 py-3 text-muted">{c.org_name || "Direct applicant"}</td>
                  <td className="px-4 py-3"><ReadinessBadge state={c.readiness_state} />{c.review_state && <span className="ml-2 text-xs text-muted">{c.review_state}</span>}</td>
                  <td className="px-4 py-3"><span className={c.open_findings ? "font-semibold text-coral" : "text-muted"}>{c.open_findings}</span></td>
                  <td className="px-4 py-3 text-xs text-muted">{fmt(c.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {d?.failed_documents?.length > 0 && (
        <section className="mt-6 rounded-md border border-hborder bg-white p-5" data-testid="ops-failed-docs">
          <h2 className="font-serif text-lg text-heading">Recent failed documents</h2>
          <ul className="mt-2 divide-y divide-hborder/70 text-sm">
            {d.failed_documents.map((x) => <li key={x.id} className="flex justify-between py-2"><span>{x.doc_type} <span className="text-xs text-muted">· case {x.case_id.slice(0, 8)}</span></span><span className="text-xs text-muted">{fmt(x.updated_at || x.created_at)}</span></li>)}
          </ul>
        </section>
      )}
    </AdminShell>
  );
}

function RunNow({ a, targets, onDone }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const job = a.run_now;
  if (!job) return null;
  const list = job === "readiness_recompute" ? targets?.cases : job === "resend_consultation_email" ? targets?.bookings : null;
  const go = async () => {
    setBusy(true);
    try {
      const body = { job };
      if (job === "readiness_recompute") body.case_id = val;
      if (job === "resend_consultation_email") body.booking_id = val;
      const { data } = await api.post("/admin/automations/run", body);
      setResult(data.result);
      toast.success("Job completed");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 border-t border-hborder pt-3" data-testid={`auto-run-${a.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        {list && <select value={val} onChange={(e) => setVal(e.target.value)} className={`${sel} max-w-[240px] py-1.5 text-xs`} data-testid={`auto-target-${a.key}`}>
          <option value="">Choose…</option>{list.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}</select>}
        <button onClick={go} disabled={busy || (list && !val)} data-testid={`auto-run-btn-${a.key}`} className="flex items-center gap-1.5 rounded-lg bg-navy px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">
          <Play size={12} /> {job === "seed_dry_run" ? "Dry run" : job === "readiness_recompute" ? "Recompute now" : "Resend email"}</button>
      </div>
      {result && <pre className="mt-2 max-h-40 overflow-auto rounded-lg bg-softcream p-2 text-[11px] text-muted" data-testid={`auto-result-${a.key}`}>{JSON.stringify(result, null, 2)}</pre>}
    </div>
  );
}

export function AdminAutomations() {
  const [items, setItems] = useState(null);
  const [targets, setTargets] = useState(null);
  const load = useCallback(() => { api.get("/admin/automations").then((r) => setItems(r.data.automations)).catch(() => setItems([])); }, []);
  useEffect(() => { load(); api.get("/admin/automations/targets").then((r) => setTargets(r.data)).catch(() => {}); }, [load]);

  return (
    <AdminShell title="Automations & integrations" subtitle="What runs automatically, how often, and whether it works">
      {items === null ? <Skeleton /> : (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="automations-grid">
          {items.map((a) => {
            const bad = a.failed_24h > 0;
            return (
              <div key={a.key} className="rounded-md border border-hborder bg-white p-5" data-testid={`auto-card-${a.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div><h3 className="font-serif text-lg text-heading">{a.name}</h3><p className="text-xs text-muted">Trigger: {a.trigger}</p></div>
                  <span className={`rounded border px-2.5 py-1 text-xs ${!a.last_run ? "border-gray-200 bg-gray-100 text-gray-600" : bad ? "border-red-300 bg-red-50 text-red-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>
                    {!a.last_run ? "No runs yet" : bad ? "Failures (24h)" : "Healthy"}</span>
                </div>
                <p className="mt-2 text-sm text-muted">{a.description}</p>
                <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                  {[["OK 24h", a.ok_24h], ["Fail 24h", a.failed_24h], ["OK 7d", a.ok_7d], ["Fail 7d", a.failed_7d]].map(([l, v]) => (
                    <div key={l} className="rounded-lg bg-softcream py-2"><p className={`font-serif text-xl ${l.startsWith("Fail") && v ? "text-coral" : "text-heading"}`}>{v}</p><p className="text-[10px] uppercase tracking-wide text-muted">{l}</p></div>
                  ))}
                </div>
                <p className="mt-3 text-xs text-muted">Last run: {a.last_run ? `${fmt(a.last_run.ts)} · ${a.last_run.status} · ${a.last_run.duration_ms} ms` : "—"}{a.avg_ms ? ` · avg ${a.avg_ms} ms` : ""}</p>
                {a.last_error && <p className="mt-1 rounded-lg bg-red-50 p-2 text-xs text-red-700" data-testid={`auto-error-${a.key}`}>Last error ({fmt(a.last_error.ts)}): {a.last_error.error}</p>}
                <RunNow a={a} targets={targets} onDone={load} />
              </div>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
}

export function AdminAudit() {
  const [f, setF] = useState({ q: "", action: "", actor_role: "", source: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(0);
  const [d, setD] = useState(null);
  const size = 25;
  const params = () => Object.fromEntries(Object.entries(f).filter(([, v]) => v));

  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/admin/audit", { params: { ...params(), skip: page * size, limit: size } }).then((r) => setD(r.data)).catch(() => setD(false));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f, page]);
  const set = (patch) => { setF((x) => ({ ...x, ...patch })); setPage(0); };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/audit/export", { params: params(), responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "holavisto-audit-log.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  return (
    <AdminShell title="Audit log" subtitle="Every audited action across the platform"
      actions={<button onClick={exportCsv} data-testid="audit-export" className="flex items-center gap-1.5 rounded-lg bg-navy px-3 py-2 text-sm font-medium text-white"><Download size={15} /> Export CSV</button>}>
      <div className="mb-4 flex flex-wrap gap-3">
        <input value={f.q} onChange={(e) => set({ q: e.target.value })} placeholder="Search action" data-testid="audit-search" className={`${sel} min-w-[180px] flex-1`} />
        <select value={f.action} onChange={(e) => set({ action: e.target.value })} className={sel} data-testid="audit-action"><option value="">All actions</option>{d?.actions?.map((a) => <option key={a}>{a}</option>)}</select>
        <select value={f.actor_role} onChange={(e) => set({ actor_role: e.target.value })} className={sel} data-testid="audit-role"><option value="">All roles</option>{["applicant", "agency_owner", "agency_manager", "agency_agent", "reviewer", "administrator", "super_administrator"].map((r) => <option key={r}>{r}</option>)}</select>
        <select value={f.source} onChange={(e) => set({ source: e.target.value })} className={sel} data-testid="audit-source"><option value="">All sources</option>{d?.sources?.map((s) => <option key={s}>{s}</option>)}</select>
        <input type="date" value={f.date_from} onChange={(e) => set({ date_from: e.target.value })} className={sel} data-testid="audit-from" aria-label="From date" />
        <input type="date" value={f.date_to} onChange={(e) => set({ date_to: e.target.value })} className={sel} data-testid="audit-to" aria-label="To date" />
      </div>
      {d === null ? <Skeleton /> : d === false ? <p className="text-sm text-coral">Could not load audit log.</p> : d.items.length === 0 ? (
        <p className="rounded-md border border-dashed border-hborder bg-white p-10 text-center text-sm text-muted" data-testid="audit-empty">No audit entries match.</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-hborder bg-white hv-scroll-thin">
            <table className="w-full min-w-[760px] text-left text-sm" data-testid="audit-table">
              <thead className="border-b border-hborder bg-softcream text-xs uppercase tracking-wider text-muted"><tr><th className="px-4 py-3">Time</th><th className="px-4 py-3">Action</th><th className="px-4 py-3">Actor</th><th className="px-4 py-3">Source</th><th className="px-4 py-3">Details</th></tr></thead>
              <tbody>
                {d.items.map((i) => (
                  <tr key={i.id} className="border-b border-hborder/60 align-top last:border-0">
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-muted">{fmt(i.created_at)}</td>
                    <td className="px-4 py-3 font-medium text-heading">{i.action}</td>
                    <td className="px-4 py-3 text-xs"><p className="text-heading">{i.actor_email || "system"}</p><p className="text-muted">{i.actor_role}</p></td>
                    <td className="px-4 py-3 text-xs text-muted">{i.source}</td>
                    <td className="max-w-xs px-4 py-3 text-xs text-muted">{i.case_id && <Link to="/admin/cases" className="text-tblue">case {i.case_id.slice(0, 8)}</Link>}{Object.keys(i.metadata || {}).length > 0 && <p className="truncate" title={JSON.stringify(i.metadata)}>{JSON.stringify(i.metadata)}</p>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center justify-between text-sm text-muted">
            <span data-testid="audit-total">{d.total} entries</span>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="audit-prev" className="rounded-md border border-hborder bg-white px-3 py-1.5 disabled:opacity-40">Previous</button>
              <button disabled={(page + 1) * size >= d.total} onClick={() => setPage(page + 1)} data-testid="audit-next" className="rounded-md border border-hborder bg-white px-3 py-1.5 disabled:opacity-40">Next</button>
            </div>
          </div>
        </>
      )}
    </AdminShell>
  );
}
